import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  FilterFn,
} from '@tanstack/react-table';
import { useAuth } from '../AuthContext';
import {
  ALL_INVENTORY_ITEMS_QUERY,
  ALL_UNITS_QUERY,
  CREATE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_ITEM_MUTATION,
} from '../graphql/inventory';
import Table from '../components/Table';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WorkIcon from '@mui/icons-material/WorkOutlined';
import './Customers.css';

interface Unit {
  id: number;
  name: string;
  abbreviation: string;
}

interface InventoryItem {
  id: number;
  name: string;
  description: string | null;
  inventoryType: 'FABRIC' | 'HARDWARE' | 'LABOR';
  costCents: number;
  quantityOnHand: number | null;
  widthInches: number | null;
  costLastUpdatedAt: string | null;
  createdAt: string;
  archivedAt: string | null;
  unitByUnitId: Unit;
}

const EMPTY_FORM = {
  name: '',
  unitId: '',
  costDollars: '',
  description: '',
};

const filterFn: FilterFn<InventoryItem> = (row, _columnId, filterValue) => {
  if (!filterValue) return true;
  const s = String(filterValue).toLowerCase();
  const item = row.original;
  return (
    item.name.toLowerCase().includes(s) ||
    (item.description || '').toLowerCase().includes(s)
  );
};

const columnHelper = createColumnHelper<InventoryItem>();

function buildColumns(
  onEdit: (item: InventoryItem) => void,
  onArchive: (item: InventoryItem) => void,
  isWorker: boolean,
) {
  return [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => (
        <span style={{ fontWeight: 600, color: '#111827' }}>{info.getValue()}</span>
      ),
      size: 260,
    }),
    columnHelper.accessor((row) => row.unitByUnitId.abbreviation, {
      id: 'unit',
      header: 'Unit',
      cell: (info) => <span style={{ color: '#6b7280' }}>{info.getValue()}</span>,
      size: 80,
    }),
    columnHelper.accessor('costCents', {
      header: 'Rate',
      cell: (info) => `$${(info.getValue() / 100).toFixed(2)}`,
      size: 110,
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: (info) => <span style={{ color: '#6b7280' }}>{info.getValue() || '—'}</span>,
      size: 300,
    }),
    ...(!isWorker
      ? [
          columnHelper.display({
            id: 'actions',
            header: '',
            cell: (info) => (
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => onEdit(info.row.original)}
                  style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0 }}
                >
                  Edit
                </button>
                <button
                  onClick={() => onArchive(info.row.original)}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0 }}
                >
                  {info.row.original.archivedAt ? 'Unarchive' : 'Archive'}
                </button>
              </div>
            ),
            size: 120,
          }),
        ]
      : []),
  ];
}

export default function Labor() {
  const { user } = useAuth();
  const isWorker = user?.role === 'worker';

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { loading, refetch } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const all: InventoryItem[] = data?.allInventoryItems?.nodes || [];
      setItems(applyFilters(all, showArchived));
    },
  });

  const { data: unitsData } = useQuery(ALL_UNITS_QUERY);
  const units: Unit[] = unitsData?.allUnits?.nodes || [];

  const [createItem] = useMutation(CREATE_INVENTORY_ITEM_MUTATION);
  const [updateItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);

  function applyFilters(all: InventoryItem[], archived: boolean) {
    return all.filter(
      (i) => i.inventoryType === 'LABOR' && (archived || !i.archivedAt),
    );
  }

  function reloadWith(archived: boolean) {
    refetch().then((res) => {
      const all: InventoryItem[] = res.data?.allInventoryItems?.nodes || [];
      setItems(applyFilters(all, archived));
    });
  }

  const handleArchive = async (item: InventoryItem) => {
    await updateItem({
      variables: {
        id: item.id,
        patch: { archivedAt: item.archivedAt ? null : new Date().toISOString() },
      },
    });
    reloadWith(showArchived);
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item: InventoryItem) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      unitId: String(item.unitByUnitId.id),
      costDollars: (item.costCents / 100).toFixed(2),
      description: item.description || '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (!form.unitId) { setFormError('Unit is required'); return; }
    const costCents = Math.round(parseFloat(form.costDollars || '0') * 100);
    if (isNaN(costCents) || costCents < 0) { setFormError('Enter a valid rate'); return; }

    setSaving(true);
    setFormError(null);
    try {
      if (editingItem) {
        await updateItem({
          variables: {
            id: editingItem.id,
            patch: {
              name: form.name.trim(),
              unitId: parseInt(form.unitId, 10),
              costCents,
              description: form.description.trim() || null,
              costLastUpdatedAt: new Date().toISOString(),
            },
          },
        });
      } else {
        await createItem({
          variables: {
            domainId: user!.domain_id,
            name: form.name.trim(),
            inventoryType: 'LABOR',
            unitId: parseInt(form.unitId, 10),
            costCents,
            description: form.description.trim() || null,
            widthInches: null,
            quantityOnHand: null,
          },
        });
      }
      closeModal();
      reloadWith(showArchived);
    } catch (err: any) {
      setFormError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const columns = buildColumns(handleOpenEdit, handleArchive, isWorker);

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: filterFn,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <div>
          <h1>Labor Rates</h1>
          <p className="subtitle">Fabrication, installation, and labor rates for auto-pricing</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                reloadWith(e.target.checked);
              }}
            />
            Show archived
          </label>
          {!isWorker && (
            <button className="primary-button btn-icon" onClick={handleOpenCreate}>
              <AddIcon fontSize="small" /> New Rate
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        {!loading && items.length === 0 && !globalFilter && (
          <div className="empty-state">
            <div className="empty-icon"><WorkIcon fontSize="inherit" /></div>
            <h2>No labor rates yet</h2>
            <p>Add labor rates to auto-fill fabrication and installation costs when creating quotes</p>
          </div>
        )}

        {(items.length > 0 || globalFilter) && (
          <>
            <div className="filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input
                type="text"
                className="filter-input"
                placeholder="Search by name or description..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
            <Table table={table} isLoading={loading} />
          </>
        )}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, width: 480, maxWidth: '90%', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {editingItem ? 'Edit Labor Rate' : 'New Labor Rate'}
              </h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }} onClick={closeModal}>
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {formError && (
                <div style={{ padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: 13 }}>
                  {formError}
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder='e.g., "Drape Fabrication Rate" — must match plugin lookup key'
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={saving}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Unit <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <select
                    style={inputStyle}
                    value={form.unitId}
                    onChange={(e) => setForm({ ...form, unitId: e.target.value })}
                    disabled={saving}
                  >
                    <option value="">Select unit...</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Rate ($) <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={inputStyle}
                    placeholder="0.00"
                    value={form.costDollars}
                    onChange={(e) => setForm({ ...form, costDollars: e.target.value })}
                    disabled={saving}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Description
                </label>
                <textarea
                  style={{ ...inputStyle, minHeight: 64, resize: 'vertical' } as React.CSSProperties}
                  placeholder="Optional notes..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  disabled={saving}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
              <button
                style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Create Rate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
